import {
  ref, watch, computed, watchEffect,
} from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';

import { useUser } from '@composables/user/useUser';
import { formatPrice } from '@utils/commonUtils';
import { LOYALTY_INFO } from '@modules/ppc/constants';

const TIER_NAMES = {
  BRONZE: 'bronze',
  SILVER: 'silver',
  GOLD: 'gold',
  PLATINUM: 'platinum',
  NON_LOYALIST: 'non-loyalist',
};
const MIN_SPEND_TO_UPGRADE = {
  [TIER_NAMES.SILVER]: 400,
  [TIER_NAMES.GOLD]: 1000,
};
const MAX_SPEND_TO_UPGRADE = {
  [TIER_NAMES.SILVER]: 500,
  [TIER_NAMES.GOLD]: 1200,
};
const POINTS_PER_DOLLAR_BY_TIER = {
  [TIER_NAMES.NON_LOYALIST]: 0,
  [TIER_NAMES.BRONZE]: 1,
  [TIER_NAMES.SILVER]: 2,
  [TIER_NAMES.GOLD]: 3,
  [TIER_NAMES.PLATINUM]: 5,
};

export const useLoyalty = () => injectLocal(LOYALTY_INFO);

export function useLoyaltyProvide() {
  const { userInfo } = useUser();

  const tierName = ref(TIER_NAMES.NON_LOYALIST);
  const isBronze = computed(() => tierName.value === TIER_NAMES.BRONZE);
  const isSilver = computed(() => tierName.value === TIER_NAMES.SILVER);
  const isGold = computed(() => tierName.value === TIER_NAMES.GOLD);
  const isPlatinum = computed(() => tierName.value === TIER_NAMES.PLATINUM);
  const isUserNonLoyalist = computed(() => tierName.value === TIER_NAMES.NON_LOYALIST);

  const isUpgradeMessageAvailable = ref(false);
  const isCardNotDefault = ref(false);
  const nextTier = ref(TIER_NAMES.BRONZE);
  const spendToNextUpgrade = ref('');
  const defaultTenderType = ref('V');
  const encryptedLoyaltyId = ref('');
  const pointsPerDollar = computed(() => POINTS_PER_DOLLAR_BY_TIER[tierName.value]);

  /** @returns {string} * */
  function _getTierName() {
    if (!userInfo.value) return TIER_NAMES.NON_LOYALIST;

    const { tierInfo } = userInfo.value;
    const userTierName = tierInfo && tierInfo.tierName;

    return userTierName.toLowerCase() || TIER_NAMES.NON_LOYALIST;
  }

  /** @returns {boolean} * */
  function _isUpgradeMessageAvailable() {
    const tierInfo = userInfo.value ? userInfo.value.tierInfo : null;
    if (!tierInfo) return false;

    const currentTier = tierName.value;
    const tiers = [TIER_NAMES.SILVER, TIER_NAMES.GOLD];

    if (tiers.includes(currentTier)) {
      const yearToDateSpend = Number(tierInfo.yearToDateSpend);
      return yearToDateSpend >= MIN_SPEND_TO_UPGRADE[currentTier] && yearToDateSpend < MAX_SPEND_TO_UPGRADE[currentTier];
    }
    return false;
  }

  /** @returns {string} * */
  function _getSpendToNextUpgrade() {
    const { tierInfo } = userInfo.value || {};

    return tierInfo && formatPrice(tierInfo.spendToNextUpgrade);
  }

  /** @returns {string} * */
  function _getDefaultTenderType() {
    return (userInfo.value && userInfo.value.defaultTenderType) || 'V';
  }

  /** @returns {string} * */
  function _getEncryptedLoyaltyId() {
    return (userInfo.value && userInfo.value.encryptedLoyaltyId) || '';
  }

  /** @returns {boolean} * */
  function _isCardNotDefault() {
    const { cardDefaultToWallet, isLtyIdLinked } = userInfo.value || {};
    return !!(!cardDefaultToWallet && isLtyIdLinked);
  }

  /**
   * @param {import('./useLoyalty').Tier} tierNameValue
   * @returns {number}
   * */
  function getPointsPerDollarByTier(tierNameValue) {
    return POINTS_PER_DOLLAR_BY_TIER[tierNameValue] || 0;
  }

  watch(userInfo, () => {
    tierName.value = _getTierName();
    isUpgradeMessageAvailable.value = _isUpgradeMessageAvailable();
    spendToNextUpgrade.value = _getSpendToNextUpgrade();
    defaultTenderType.value = _getDefaultTenderType();
    encryptedLoyaltyId.value = _getEncryptedLoyaltyId();
    isCardNotDefault.value = _isCardNotDefault();
  }, { deep: true });

  watchEffect(() => {
    nextTier.value = {
      [TIER_NAMES.NON_LOYALIST]: TIER_NAMES.BRONZE,
      [TIER_NAMES.BRONZE]: TIER_NAMES.SILVER,
      [TIER_NAMES.SILVER]: TIER_NAMES.GOLD,
      [TIER_NAMES.GOLD]: TIER_NAMES.PLATINUM,
      [TIER_NAMES.PLATINUM]: TIER_NAMES.PLATINUM,
    }[tierName.value];
  });

  provideLocal(LOYALTY_INFO, {
    tierName,
    isBronze,
    isSilver,
    isGold,
    isPlatinum,
    isUserNonLoyalist,
    isUpgradeMessageAvailable,
    isCardNotDefault,
    nextTier,
    spendToNextUpgrade,
    defaultTenderType,
    encryptedLoyaltyId,
    pointsPerDollar,
    getPointsPerDollarByTier,
  });
}
